/* ==========================================================================
   Sharky — motion layer
   Dependency-free. Everything here is enhancement: sections render complete
   without this file, and with reduced motion only opacity fades remain.

   Scroll container: below 990px the document scrolls; from 990px Horizon makes
   .page-wrapper the scroller. Capture-phase scroll listening and
   getBoundingClientRect cover both, so positions never depend on which it is.

   Responsibilities
     1. A single rAF driver for everything tied to scroll position
     2. Word-by-word headline reveal  [data-sk-split]
     3. Hero parallax and pointer tilt  [data-sk-dive]
     4. Section progress  [data-sk-progress]  (--sk-p) and lit statements [data-sk-scrub]
     5. Pinned anatomy scene  [data-sk-anatomy]
     6. Pinned horizontal rail  [data-sk-rail]
     7. Price count-up  [data-sk-count]
     8. Video playback  [data-sk-reels]
     9. Ticker speed-up on scroll  [data-sk-ticker]
    10. Magnetic buttons  [data-sk-magnetic]
    11. Swipe rows  [data-sk-swipe]: focus on the centred card and a progress bar
    12. Reading progress line at the top of the screen

   Theme editor: everything stays static so merchants edit what they see.
   ========================================================================== */

(() => {
  'use strict';

  const doc = document;
  const reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const wideQuery = window.matchMedia('(min-width: 990px)');
  const pointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);

  const fullMotion = () => !reduceQuery.matches && !designMode;
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const mix = (a, b, t) => a + (b - a) * t;
  const smooth = (edge0, edge1, x) => {
    const t = clamp((x - edge0) / (edge1 - edge0));
    return t * t * (3 - 2 * t);
  };
  const $$ = (selector, context = doc) => Array.from(context.querySelectorAll(selector));
  const viewportHeight = () => window.innerHeight || doc.documentElement.clientHeight;

  /* The element that scrolls, for programmatic jumps only. */
  const scroller = () => {
    const wrapper = doc.querySelector('.page-wrapper');
    if (wrapper && /auto|scroll/.test(getComputedStyle(wrapper).overflowY)) return wrapper;
    return window;
  };

  const scrollTopOf = (target) => (target === window ? window.scrollY || 0 : target.scrollTop);

  /* --- 1. Driver -------------------------------------------------------- */
  const driver = (() => {
    const items = new Map();
    let frame = 0;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const item = items.get(entry.target);
          if (!item) continue;
          item.visible = entry.isIntersecting;
          if (item.visible) request();
        }
      },
      { rootMargin: '15% 0px' }
    );

    const tick = () => {
      frame = 0;
      let again = false;
      for (const item of items.values()) {
        if (item.visible && item.update() === true) again = true;
      }
      if (again) request();
    };

    function request() {
      if (!frame) frame = window.requestAnimationFrame(tick);
    }

    doc.addEventListener('scroll', request, { passive: true, capture: true });
    window.addEventListener('resize', request, { passive: true });

    return {
      request,
      /** @param {Element} el @param {() => boolean | void} update Return true to keep animating. */
      add(el, update) {
        items.set(el, { update, visible: false });
        observer.observe(el);
      },
      remove(el) {
        items.delete(el);
        observer.unobserve(el);
      },
    };
  })();

  /* --- 2. Headline split ------------------------------------------------ */
  const splitHeading = (heading) => {
    if (heading.classList.contains('is-split')) return;

    if (!fullMotion()) {
      heading.classList.add('is-split');
      return;
    }

    heading.setAttribute('aria-label', heading.textContent.replace(/\s+/g, ' ').trim());
    let index = Number(heading.dataset.skSplitStart || 0);

    const wrapWords = (node) => {
      for (const child of Array.from(node.childNodes)) {
        if (child.nodeType === Node.ELEMENT_NODE) {
          if (child.tagName !== 'BR') wrapWords(child);
          continue;
        }
        if (child.nodeType !== Node.TEXT_NODE || !child.textContent.trim()) continue;

        const fragment = doc.createDocumentFragment();
        for (const part of child.textContent.split(/(\s+)/)) {
          if (!part) continue;
          if (/^\s+$/.test(part)) {
            fragment.append(doc.createTextNode(' '));
            continue;
          }
          const outer = doc.createElement('span');
          outer.className = 'sk-word';
          outer.setAttribute('aria-hidden', 'true');
          const inner = doc.createElement('span');
          inner.className = 'sk-word__inner';
          inner.style.setProperty('--sk-w', String(index));
          inner.textContent = part;
          outer.append(inner);
          fragment.append(outer);
          index += 1;
        }
        child.replaceWith(fragment);
      }
    };

    wrapWords(heading);
    heading.classList.add('is-split');
  };

  const initSplit = () => {
    const headings = $$('[data-sk-split]');
    headings.forEach(splitHeading);

    if (designMode || !('IntersectionObserver' in window)) {
      headings.forEach((heading) => heading.classList.add('is-in'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -6% 0px', threshold: 0.05 }
    );
    headings.forEach((heading) => observer.observe(heading));
  };

  /* --- 3. Hero ---------------------------------------------------------- */
  const initHero = () => {
    if (!fullMotion()) return;

    for (const hero of $$('[data-sk-dive]')) {
      let targetX = 0;
      let targetY = 0;
      let x = 0;
      let y = 0;

      driver.add(hero, () => {
        const rect = hero.getBoundingClientRect();
        hero.style.setProperty('--sk-p', clamp(-rect.top / Math.max(rect.height, 1)).toFixed(4));

        x += (targetX - x) * 0.07;
        y += (targetY - y) * 0.07;
        hero.style.setProperty('--sk-mx', x.toFixed(4));
        hero.style.setProperty('--sk-my', y.toFixed(4));
        return Math.abs(targetX - x) > 0.002 || Math.abs(targetY - y) > 0.002;
      });

      if (!pointerQuery.matches) continue;

      hero.addEventListener(
        'pointermove',
        (event) => {
          const rect = hero.getBoundingClientRect();
          targetX = clamp(((event.clientX - rect.left) / rect.width) * 2 - 1, -1, 1);
          targetY = clamp(((event.clientY - rect.top) / rect.height) * 2 - 1, -1, 1);
          driver.request();
        },
        { passive: true }
      );

      hero.addEventListener('pointerleave', () => {
        targetX = 0;
        targetY = 0;
        driver.request();
      });
    }
  };

  /* --- 4. Section progress ---------------------------------------------- */
  const initProgress = () => {
    if (!fullMotion()) return;

    for (const section of $$('[data-sk-progress]')) {
      // "pin": a tall section whose stage sticks to the screen; 0 at the pin, 1 at its release.
      const pinned = section.dataset.skProgress === 'pin';
      if (pinned) section.classList.add('is-pinned');

      driver.add(section, () => {
        const rect = section.getBoundingClientRect();
        const vh = viewportHeight();
        const value = pinned
          ? -rect.top / Math.max(rect.height - vh, 1)
          : (vh - rect.top) / Math.max(Math.min(rect.height, vh), 1);
        section.style.setProperty('--sk-p', clamp(value).toFixed(4));
      });
    }
  };

  /* --- 4b. Statement lit word by word by scroll -------------------------- */
  const initScrub = () => {
    if (!fullMotion()) return;

    for (const statement of $$('[data-sk-scrub]')) {
      const fragment = doc.createDocumentFragment();
      const words = [];
      for (const part of statement.textContent.trim().split(/(\s+)/)) {
        if (/^\s+$/.test(part)) {
          fragment.append(doc.createTextNode(' '));
          continue;
        }
        const word = doc.createElement('span');
        word.className = 'sk-scrub-word';
        word.textContent = part;
        words.push(word);
        fragment.append(word);
      }
      statement.replaceChildren(fragment);

      driver.add(statement, () => {
        const rect = statement.getBoundingClientRect();
        const vh = viewportHeight();
        // Starts as the statement enters the lower part of the screen, ends above the middle.
        const progress = clamp((vh * 0.86 - rect.top) / Math.max(vh * 0.52, 1));
        words.forEach((word, index) => {
          const local = clamp(progress * (words.length + 3) - index, 0, 1);
          word.style.opacity = String(mix(0.16, 1, local));
        });
      });
    }
  };

  /* --- 5. Anatomy ------------------------------------------------------- */
  const initAnatomy = () => {
    if (!fullMotion()) return;

    for (const section of $$('[data-sk-anatomy]')) {
      const steps = $$('.sk-anatomy__step', section);
      const cam = section.querySelector('.sk-anatomy__cam');
      const rail = section.querySelector('.sk-anatomy__rail');
      const dots = $$('[data-sk-step]', section);
      if (steps.length < 2 || !cam) continue;

      const stops = steps.map((step) => ({
        x: Number(step.dataset.fx) / 100,
        y: Number(step.dataset.fy) / 100,
        z: Number(step.dataset.zoom) / 100,
        r: step.dataset.side === 'back' ? 180 : 0,
        tint: step.dataset.tint,
      }));

      section.classList.add('is-pinned');
      if (rail) rail.hidden = false;
      for (const step of steps) {
        step.removeAttribute('data-sk-reveal');
        step.classList.add('is-in');
      }

      const current = { x: stops[0].x, y: stops[0].y, z: stops[0].z, r: stops[0].r };
      let active = -1;
      let resting = false;

      const setActive = (index) => {
        if (index === active) return;
        active = index;
        steps.forEach((step, i) => {
          step.classList.toggle('is-active', i === index);
          step.setAttribute('aria-hidden', String(i !== index));
        });
        dots.forEach((dot, i) => {
          if (i === index) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
        section.style.setProperty('--sk-anatomy-bg', stops[index].tint);
      };

      const progressOf = () => {
        const rect = section.getBoundingClientRect();
        return clamp(-rect.top / Math.max(rect.height - viewportHeight(), 1));
      };

      driver.add(section, () => {
        const progress = progressOf();
        const position = progress * (stops.length - 1);
        const from = Math.min(Math.floor(position), stops.length - 2);
        const local = position - from;
        // The camera rests near each stop and travels in between.
        const t = smooth(0.28, 0.72, local);
        const a = stops[from];
        const b = stops[from + 1];

        const ease = 0.12;
        current.x += (mix(a.x, b.x, t) - current.x) * ease;
        current.y += (mix(a.y, b.y, t) - current.y) * ease;
        current.z += (mix(a.z, b.z, t) - current.z) * ease;
        current.r += (mix(a.r, b.r, t) - current.r) * ease;

        cam.style.setProperty('--sk-cam-x', current.x.toFixed(4));
        cam.style.setProperty('--sk-cam-y', current.y.toFixed(4));
        cam.style.setProperty('--sk-cam-z', current.z.toFixed(4));
        cam.style.setProperty('--sk-cam-r', current.r.toFixed(2));
        section.style.setProperty('--sk-anatomy-p', progress.toFixed(4));

        setActive(Math.round(position));

        const isResting = t <= 0.001 || t >= 0.999;
        if (isResting !== resting) {
          resting = isResting;
          section.classList.toggle('is-resting', resting);
        }

        const target = mix(a.x, b.x, t);
        return (
          Math.abs(target - current.x) > 0.0005 ||
          Math.abs(mix(a.z, b.z, t) - current.z) > 0.0008 ||
          Math.abs(mix(a.r, b.r, t) - current.r) > 0.05
        );
      });

      dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          const target = scroller();
          const rect = section.getBoundingClientRect();
          const start = rect.top + scrollTopOf(target);
          const span = rect.height - viewportHeight();
          target.scrollTo({ top: start + (span * index) / (stops.length - 1), behavior: 'smooth' });
        });
      });

      setActive(0);
    }
  };

  /* --- 6. Rail ---------------------------------------------------------- */
  const initRail = () => {
    if (!fullMotion()) return;

    for (const section of $$('[data-sk-rail]')) {
      const track = section.querySelector('.sk-rail__track');
      if (!track) continue;
      const images = $$('.sk-rail__media', section);
      let distance = 0;
      let current = 0;

      const measure = () => {
        if (!wideQuery.matches) {
          section.classList.remove('is-pinned');
          section.style.removeProperty('height');
          track.style.removeProperty('transform');
          distance = 0;
          return;
        }
        section.classList.add('is-pinned');
        distance = Math.max(track.scrollWidth - window.innerWidth, 0);
        section.style.height = `${distance + viewportHeight()}px`;
        driver.request();
      };

      driver.add(section, () => {
        if (!distance) return false;
        const rect = section.getBoundingClientRect();
        const target = clamp(-rect.top / Math.max(rect.height - viewportHeight(), 1)) * distance;
        current += (target - current) * 0.14;
        track.style.transform = `translate3d(${(-current).toFixed(1)}px, 0, 0)`;

        const width = window.innerWidth;
        for (const media of images) {
          const box = media.getBoundingClientRect();
          const offset = (box.left + box.width / 2 - width / 2) / width;
          media.style.setProperty('--sk-rx', `${(offset * -26).toFixed(1)}px`);
        }
        return Math.abs(target - current) > 0.4;
      });

      // Keep keyboard focus visible: a focused link inside the moving track scrolls the page instead.
      track.addEventListener('focusin', (event) => {
        if (!distance || !(event.target instanceof Element)) return;
        const target = scroller();
        const rect = section.getBoundingClientRect();
        const item = event.target.closest('.sk-rail__end, .sk-rail__card');
        if (!item) return;
        const within = item.offsetLeft / Math.max(track.scrollWidth, 1);
        target.scrollTo({ top: rect.top + scrollTopOf(target) + (rect.height - viewportHeight()) * within, behavior: 'auto' });
      });

      measure();
      new ResizeObserver(measure).observe(track);
      wideQuery.addEventListener?.('change', measure);
    }
  };

  /* --- 7. Count-up ------------------------------------------------------ */
  const initCount = () => {
    const prices = $$('[data-sk-count]');
    if (!prices.length || !fullMotion()) return;

    const animate = (el) => {
      const original = el.textContent.trim();
      const match = original.match(/^(\D*)([\d.,\s]*\d)(\D*)$/);
      if (!match) return;
      const [, prefix, digits, suffix] = match;
      const decimalMatch = digits.match(/([.,])(\d{2})$/);
      const separator = decimalMatch ? decimalMatch[1] : '';
      const decimals = decimalMatch ? 2 : 0;
      const value = Number(digits.replace(/[^\d]/g, '')) / (decimals ? 100 : 1);
      const started = performance.now();
      const duration = 1100;

      const step = (now) => {
        const t = clamp((now - started) / duration);
        const eased = 1 - Math.pow(1 - t, 4);
        if (t >= 1) {
          el.textContent = original;
          return;
        }
        let text = (value * eased).toFixed(decimals);
        if (separator) text = text.replace('.', separator);
        el.textContent = `${prefix}${text}${suffix}`;
        window.requestAnimationFrame(step);
      };
      window.requestAnimationFrame(step);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          animate(entry.target);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.6 }
    );
    prices.forEach((price) => observer.observe(price));
  };

  /* --- 8. Reels --------------------------------------------------------- */
  const initReels = () => {
    for (const section of $$('[data-sk-reels]')) {
      const videos = $$('video', section);
      const toggle = section.querySelector('[data-sk-reels-toggle]');
      if (!videos.length) continue;

      if (!fullMotion()) {
        for (const video of videos) {
          video.controls = true;
          video.removeAttribute('autoplay');
          video.pause();
        }
        continue;
      }

      let paused = false;
      const inView = new Set();

      const sync = () => {
        for (const video of videos) {
          if (!paused && inView.has(video)) {
            video.muted = true;
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        }
      };

      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) inView.add(entry.target);
            else inView.delete(entry.target);
          }
          sync();
        },
        { threshold: 0.35 }
      );
      videos.forEach((video) => observer.observe(video));

      if (toggle) {
        toggle.hidden = false;
        toggle.addEventListener('click', () => {
          paused = !paused;
          toggle.setAttribute('aria-pressed', String(paused));
          toggle.textContent = paused ? toggle.dataset.play : toggle.dataset.pause;
          sync();
        });
      }
    }
  };

  /* --- 9. Ticker -------------------------------------------------------- */
  const initTicker = () => {
    if (!fullMotion()) return;

    for (const ticker of $$('[data-sk-ticker]')) {
      const track = ticker.querySelector('.sk-ticker__track');
      const animation = track?.getAnimations?.()[0];
      if (!animation) continue;

      let last = null;
      let rate = 1;

      driver.add(ticker, () => {
        const top = ticker.getBoundingClientRect().top;
        const delta = last === null ? 0 : Math.abs(top - last);
        last = top;
        rate = Math.max(1, Math.min(6, Math.max(rate * 0.94, 1 + delta * 0.12)));
        animation.playbackRate = rate;
        return rate > 1.01;
      });
    }
  };

  /* --- 10. Magnetic buttons --------------------------------------------- */
  const initMagnetic = () => {
    if (!fullMotion() || !pointerQuery.matches) return;

    for (const button of $$('[data-sk-magnetic]')) {
      button.addEventListener(
        'pointermove',
        (event) => {
          const rect = button.getBoundingClientRect();
          const x = (event.clientX - rect.left - rect.width / 2) * 0.16;
          const y = (event.clientY - rect.top - rect.height / 2) * 0.28;
          button.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0)`;
        },
        { passive: true }
      );
      button.addEventListener('pointerleave', () => button.style.removeProperty('transform'));
    }
  };

  /* --- 11. Swipe rows ---------------------------------------------------- */
  const initSwipe = () => {
    for (const row of $$('[data-sk-swipe]')) {
      const bar = row.closest('section')?.querySelector('.sk-swipe-bar');
      const fill = bar?.querySelector('i');
      const items = Array.from(row.children);
      let frame = 0;

      const update = () => {
        frame = 0;
        const max = row.scrollWidth - row.clientWidth;
        const scrolls = row.clientWidth > 0 && max > 1;
        if (bar) bar.hidden = !scrolls;
        if (!scrolls) {
          for (const item of items) item.style.removeProperty('--sk-c');
          return;
        }

        const visible = row.clientWidth / row.scrollWidth;
        if (fill) fill.style.setProperty('--sk-swipe', mix(visible, 1, row.scrollLeft / max).toFixed(3));
        if (!fullMotion()) return;

        const box = row.getBoundingClientRect();
        const middle = box.left + box.width / 2;
        for (const item of items) {
          const rect = item.getBoundingClientRect();
          const distance = Math.abs(rect.left + rect.width / 2 - middle) / box.width;
          item.style.setProperty('--sk-c', clamp(distance * 1.25).toFixed(3));
        }
      };

      const request = () => {
        if (!frame) frame = window.requestAnimationFrame(update);
      };

      row.addEventListener('scroll', request, { passive: true });
      window.addEventListener('resize', request, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(request).observe(row);
      update();
    }
  };

  /* --- 12. Reading progress ---------------------------------------------- */
  const initReading = () => {
    if (!fullMotion() || !doc.querySelector('[data-sk-dive], [data-sk-anatomy]')) return;

    const line = doc.createElement('div');
    line.className = 'sk-progress';
    line.setAttribute('aria-hidden', 'true');
    doc.body.append(line);

    driver.add(doc.body, () => {
      const target = scroller();
      const box = target === window ? doc.documentElement : target;
      const span = box.scrollHeight - box.clientHeight;
      line.style.setProperty('--sk-read', span > 0 ? clamp(box.scrollTop / span).toFixed(4) : '0');
    });
  };

  /* --- Boot ------------------------------------------------------------- */
  const boot = () => {
    initSplit();
    initHero();
    initProgress();
    initScrub();
    initAnatomy();
    initRail();
    initCount();
    initReels();
    initTicker();
    initMagnetic();
    initSwipe();
    initReading();
    driver.request();
  };

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
